/**
 * Parameter Visibility Utilities
 *
 * Evaluates conditional parameter visibility based on x-ms-input-dependencies.
 * Used to show/hide parameters based on the values of other parameters.
 */

import type { ParameterInfo, InputDependency } from '@/types/operationParameter';
import { InputDependencyType } from '@/types/operationParameter';
import type { SchemaObject } from '@/types/valueSegment';
import { isObject } from '@/utils/primitives';

/**
 * Parser injected by callers to convert a raw `x-ms-input-dependencies`
 * value (either manifest or swagger format) into a normalized array of
 * dependency descriptors. Callers wire in `parseInputDependencies` from
 * `services/operationManifestService` — this indirection keeps
 * `@/utils/*` free of inward dependencies on `@/services/*`. The return
 * type is intentionally loose so the caller's `ParsedInputDependency[]`
 * shape (which is structurally compatible with `InputDependency`)
 * satisfies it without a hard re-import.
 */
export type ParseInputDependencies = (rawInputDeps: unknown) => readonly unknown[];

/**
 * Evaluate whether a parameter should be visible based on input dependencies.
 *
 * Logic:
 * - If no visibility dependencies exist, parameter is visible
 * - Multiple dependencies use AND logic (all must be satisfied)
 * - Within a dependency's `values` array, OR logic applies (any value matches)
 * - `excludeValues` uses inverse OR logic (visible if value doesn't match any)
 *
 * @param parameter - The parameter to evaluate visibility for
 * @param allParameterValues - Current values of all parameters (keyed by parameter name)
 * @returns true if the parameter should be shown, false if hidden
 */
export function shouldShowParameter(
  parameter: ParameterInfo,
  allParameterValues: Record<string, unknown>
): boolean {
  return isVisibleByDependencies(parameter.info?.inputDependencies, allParameterValues);
}

/**
 * Evaluate a list of input dependencies against the current parameter values
 * without requiring a full ParameterInfo. Useful at serialization time when
 * we only have the manifest schema and need to know if a property is currently
 * visible (and therefore should be serialized).
 *
 * Logic:
 * - If no visibility dependencies exist, parameter is visible
 * - Multiple dependencies use AND logic (all must be satisfied)
 * - Within a dependency's `values` array, OR logic applies (any value matches)
 * - `excludeValues` uses inverse OR logic (visible if value doesn't match any)
 *
 * @param dependencies - Parsed input dependencies (or undefined)
 * @param parameterValues - Current values of all parameters
 * @returns true if visible (no deps, or all visibility deps satisfied)
 */
export function isVisibleByDependencies(
  dependencies: InputDependency[] | undefined,
  parameterValues: Record<string, unknown>
): boolean {
  if (!dependencies || dependencies.length === 0) {
    return true;
  }

  const visibilityDeps = dependencies.filter(
    (dep) => dep.type === InputDependencyType.Visibility
  );

  if (visibilityDeps.length === 0) {
    return true;
  }

  return visibilityDeps.every((dep) =>
    evaluateDependency(dep, parameterValues)
  );
}

/**
 * Get a value from a nested path in an object.
 * Supports dot notation (e.g., 'agentModelSettings.agentHistoryReductionSettings.agentHistoryReductionType')
 *
 * @param obj - The object to traverse
 * @param path - Dot-separated path to the value
 * @returns The value at the path, or undefined if not found
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');

  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Evaluate a single input dependency.
 *
 * @param dependency - The dependency to evaluate
 * @param parameterValues - Current values of all parameters
 * @returns true if dependency is satisfied (parameter should show)
 */
function evaluateDependency(
  dependency: InputDependency,
  parameterValues: Record<string, unknown>
): boolean {
  // Support both flat keys and nested paths (e.g., 'agentModelType' or 'settings.mode')
  const paramRef = dependency.parameterReference;
  let currentValue: unknown;

  // First try direct key lookup
  if (paramRef in parameterValues) {
    currentValue = parameterValues[paramRef];
  } else if (paramRef.includes('.')) {
    // Try nested path lookup
    currentValue = getNestedValue(parameterValues, paramRef);
  } else {
    currentValue = undefined;
  }

  // If values array is specified, check if current value matches any
  if (dependency.values && dependency.values.length > 0) {
    const result = dependency.values.some((expectedValue) =>
      valuesMatch(currentValue, expectedValue)
    );
    return result;
  }

  // If excludeValues array is specified, check if current value doesn't match any
  if (dependency.excludeValues && dependency.excludeValues.length > 0) {
    const result = !dependency.excludeValues.some((excludedValue) =>
      valuesMatch(currentValue, excludedValue)
    );
    return result;
  }

  // No conditions specified = satisfied (show parameter)
  return true;
}

/**
 * Compare two values for equality.
 * Handles string comparison case-insensitively.
 *
 * @param actual - The actual parameter value
 * @param expected - The expected value from dependency config
 * @returns true if values match
 */
function valuesMatch(actual: unknown, expected: unknown): boolean {
  // Direct equality
  if (actual === expected) {
    return true;
  }

  // Case-insensitive string comparison
  if (typeof actual === 'string' && typeof expected === 'string') {
    return actual.toLowerCase() === expected.toLowerCase();
  }

  // Handle null/undefined cases
  if (actual == null && expected == null) {
    return true;
  }

  // Compare as JSON for objects/arrays
  if (typeof actual === 'object' && typeof expected === 'object') {
    try {
      return JSON.stringify(actual) === JSON.stringify(expected);
    } catch {
      return false;
    }
  }

  // Handle number/string comparison (e.g., "123" vs 123)
  if (
    (typeof actual === 'string' && typeof expected === 'number') ||
    (typeof actual === 'number' && typeof expected === 'string')
  ) {
    return String(actual) === String(expected);
  }

  return false;
}

// =============================================================================
// MANIFEST SCHEMA INTROSPECTION
// =============================================================================
//
// These helpers walk a manifest's `inputs` SchemaObject (recursively, including
// nested `properties`) to collect serialization-time information about which
// fields should be hidden for the active configuration and which fields
// declare a default value. They power the visibility-aware serializer in
// `parameterUtils.ts`.

/**
 * Recursively walk a manifest schema and collect every leaf property whose
 * `x-ms-input-dependencies` visibility rules are NOT satisfied by the current
 * parameter values. Hidden parents propagate to all descendants.
 *
 * Used at serialization time to exclude properties that the editor hides for
 * the active configuration (e.g. `modelId` and `headers` on an Agent node
 * with `agentModelType: 'AzureOpenAI'`).
 *
 * @param schema - Schema object with a `properties` map (e.g. `manifest.properties.inputs`)
 * @param parameterValues - Current values from the editor
 * @param parseInputDependencies - Injected parser from
 *   `services/operationManifestService`; converts the raw
 *   `x-ms-input-dependencies` value to the normalized array shape.
 * @returns Set of dot-notation flat keys that should be hidden
 */
export function collectHiddenSchemaKeys(
  schema: SchemaObject | undefined,
  parameterValues: Record<string, unknown>,
  parseInputDependencies: ParseInputDependencies
): Set<string> {
  const hidden = new Set<string>();
  walkSchemaProperties(schema, '', false, (flatKey, propSchema, parentHidden) => {
    let isHidden = parentHidden;
    if (!isHidden) {
      const inputDeps = propSchema['x-ms-input-dependencies'];
      if (inputDeps !== undefined) {
        const parsed = parseInputDependencies(inputDeps) as InputDependency[];
        if (!isVisibleByDependencies(parsed, parameterValues)) {
          isHidden = true;
        }
      }
    }
    if (isHidden) hidden.add(flatKey);
    return isHidden;
  });
  return hidden;
}

/**
 * Recursively walk a manifest schema and collect every leaf property that
 * declares a `default` value, keyed by its dot-notation path. Visibility is
 * intentionally NOT evaluated here — callers decide whether to apply each
 * default based on whether the property is currently visible.
 *
 * The returned map preserves the manifest's literal default values
 * (strings, numbers, booleans, etc.) and does not clone them.
 */
export function collectSchemaDefaults(
  schema: SchemaObject | undefined
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  walkSchemaProperties(schema, '', false, (flatKey, propSchema) => {
    if (propSchema.default !== undefined) {
      defaults[flatKey] = propSchema.default;
    }
    return false;
  });
  return defaults;
}

/**
 * Returns true if the dot-notation `path` (or any of its ancestor paths) is
 * present in `hiddenKeys`. Used to skip writes for properties whose
 * visibility rules — or whose ancestors' visibility rules — exclude them
 * from serialization for the active configuration.
 */
export function isPathOrAncestorHidden(path: string, hiddenKeys: Set<string>): boolean {
  if (hiddenKeys.has(path)) return true;
  const segments = path.split('.');
  for (let i = 1; i < segments.length; i++) {
    if (hiddenKeys.has(segments.slice(0, i).join('.'))) return true;
  }
  return false;
}

/**
 * Generic recursive walker over a SchemaObject's nested `properties`.
 * Invokes `visit(flatKey, propSchema, parentHidden)` for each property and
 * uses its return value as the `parentHidden` flag for descendants.
 */
function walkSchemaProperties(
  schema: SchemaObject | undefined,
  prefix: string,
  parentHidden: boolean,
  visit: (flatKey: string, propSchema: SchemaObject, parentHidden: boolean) => boolean
): void {
  const props = schema?.properties;
  if (!props || !isObject(props)) return;

  for (const [propName, propSchema] of Object.entries(props)) {
    if (!isObject(propSchema)) continue;
    const flatKey = prefix ? `${prefix}.${propName}` : propName;
    const childHidden = visit(flatKey, propSchema, parentHidden);
    walkSchemaProperties(propSchema, flatKey, childHidden, visit);
  }
}
